import {Language} from "@/api/data-contracts";
import dataDeJson from "./landing-page.de.json";
import dataEnJson from "./landing-page.en.json";
import dataPlJson from "./landing-page.pl.json";
import dataItJson from "./landing-page.it.json";
import dataFrJson from "./landing-page.fr.json";
import dataCsJson from "./landing-page.cs.json";

export interface TestimonyDTO {
    name: string;
    text: string;
    image: string;
}

export interface LogoDTO {
    alt: string;
    image: string;
}

export interface ContactDataDTO {
    name: string;
    role: string;
    phone: string;
    email: string;
}

export interface Section1DTO {
    title: string;
    subtitle: string;
    intro: string;
    cta: string;
    bulletPoints: string[];
    logos: LogoDTO[];
}

export interface Section1aDTO {
    title: string;
    testimony: TestimonyDTO;
    info: string;
    message: string;
}

export interface CooperationMessageDTO {
    title: string;
    message: string | string[];
}

export interface Section2DTO {
    title: string;
    subTitle: string;
    tabs: string[];
    cooperation: CooperationMessageDTO[][];
}

export interface Section3DTO {
    title: string;
    bulletPoints: string[];
}

export interface Section4DTO {
    title: string;
    stepLabel: string;
    steps: string[];
}

export interface Section5DTO {
    title: string;
    testimonies: TestimonyDTO[];
}

export interface Section6DTO {
    title: string;
    text: string;
}

export interface Section7DTO {
    title: string;
    text: string;
    cta: string;
}

export interface ContactWithImageDTO extends ContactDataDTO {
    image: string;
}

export interface Section8DTO {
    title: string;
    text: string;
    contacts: ContactWithImageDTO[];
    primaryContact?: ContactDataDTO;
}

// Main interface for the entire landing page data
export interface LandingPageData {
    section1: Section1DTO;
    section1a: Section1aDTO;
    section2: Section2DTO;
    section3: Section3DTO;
    section4: Section4DTO;
    section5: Section5DTO;
    section6: Section6DTO;
    section7: Section7DTO;
    section8: Section8DTO;
}

// Typed bindings of the imported dictionaries. Any drift between the JSON files and the
// LandingPageData interface surfaces as a compile-time error here instead of a runtime surprise.
const dataDe = dataDeJson satisfies LandingPageData;
const dataEn = dataEnJson satisfies LandingPageData;
const dataPl = dataPlJson satisfies LandingPageData;
const dataIt = dataItJson satisfies LandingPageData;
const dataFr = dataFrJson satisfies LandingPageData;
const dataCs = dataCsJson satisfies LandingPageData;

/**
 * Loads the landing page data for the given display language. The homepage copy lives locally
 * in the frontend as DE/EN/PL/IT/FR/CS dictionaries (not via the runtime i18n key store). Each of
 * these languages gets its own variant; any other value gets the English variant, matching the
 * English default display language for anonymous visitors.
 */
export function loadLandingPageData(language: Language): LandingPageData {
    switch (language) {
        case Language.GERMAN:
            return dataDe;
        case Language.POLISH:
            return dataPl;
        case Language.ITALIAN:
            return dataIt;
        case Language.FRENCH:
            return dataFr;
        case Language.CZECH:
            return dataCs;
        default:
            return dataEn;
    }
}

/**
 * Loads the primary contact data. Contact details are proper names and addresses, so they are
 * language-independent and always sourced from the German variant.
 */
export function loadContactData(): ContactDataDTO {
    const section8 = dataDe.section8;
    return section8.primaryContact || section8.contacts[0];
}
